/**
 * Financial Dashboard Page
 * 
 * Modern, clean dashboard for the Librattum MAX financial module.
 */
import type { TooltipProps } from 'recharts';
import { useState } from 'react';
import { format } from 'date-fns';
import { PiggyBank, Calendar, AlertTriangle, Target, Lock } from 'lucide-react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  KPICard,
  PeriodNavigator,
  ProgressIndicator,
  DualCalendar,
  RecentTransactions,
  FinancialSummaryCards,
} from '@/components/financial';
import {
  useDashboard,
  useCurrentBudgetPeriod,
  useBudgetPeriodNavigation,
  useNavigatePeriod,
  useFinancialAlerts,
  useOverdueExpenses,
  useFutureExpenses,
  useRevenues,
  useMonthlyExpenses,
} from '@/hooks/use-financial';
import { FeatureGuard } from '@/hooks/use-plan-features';
import { FeatureBlockedMessage, FeatureLockedCard } from '@/components/subscription/FeatureBlockedPage';
import { formatBrasilia } from '@/lib/brasiliaDate';

/** Meta com progresso: cor da barra por faixa. */
const esquemaDeProgresso = (progresso: number): 'success' | 'default' | 'warning' => {
  if (progresso >= 100) return 'success';
  if (progresso >= 50) return 'default';
  return 'warning';
};

/** `achieved` e progresso >= 100 são tratados como o mesmo estado — a meta pode
 *  estar cumprida sem o status ter sido atualizado ainda. */
const metaCumprida = (meta: { status?: string; progress: number }): boolean =>
  meta.status === 'achieved' || meta.progress >= 100;

const classeDeStatusDaMeta = (meta: { status?: string; progress: number }): string => {
  if (metaCumprida(meta)) return 'text-emerald-600 font-medium';
  if (meta.status === 'active') return 'text-primary';
  return 'text-amber-600';
};

const rotuloDeStatusDaMeta = (meta: { status?: string; progress: number }): string => {
  if (metaCumprida(meta)) return '✓ Alcançada';
  if (meta.status === 'active') return 'Em progresso';
  return 'Pausada';
};

// Modern chart colors
const COLORS = {
  primary: 'hsl(174, 62%, 38%)',
  primaryLight: 'hsl(174, 62%, 50%)',
  success: 'hsl(152, 69%, 40%)',
  warning: 'hsl(38, 92%, 50%)',
  danger: 'hsl(0, 72%, 51%)',
  muted: 'hsl(220, 14%, 80%)',
};

// Currency formatter for charts
const formatCurrency = (value: number | undefined | null) => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return 'R$ 0,00';
  }
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    notation: value > 100000 ? 'compact' : 'standard',
  }).format(value);
};

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (active && payload?.length) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-lg p-3">
        <p className="text-sm font-medium text-foreground mb-1">{label}</p>
        {payload.map((entry, index: number) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export function FinancialDashboard() {
  
  const { data: currentPeriod, isLoading: isPeriodLoading } = useCurrentBudgetPeriod();
  const { data: navigation } = useBudgetPeriodNavigation();
  const { mutate: navigatePeriod, isPending: isNavigating } = useNavigatePeriod();
  const { data: dashboardData, isLoading: isDashboardLoading } = useDashboard(currentPeriod?.id);
  const { data: alerts } = useFinancialAlerts();
  const { data: overdueExpenses } = useOverdueExpenses(currentPeriod?.id);
  const { data: futureExpenses } = useFutureExpenses('active', currentPeriod?.id);
  const { data: revenues } = useRevenues(currentPeriod?.id);
  const { data: monthlyExpenses } = useMonthlyExpenses(currentPeriod?.id);
  
  // State for calendar selection
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date | undefined>(undefined);

  const isLoading = isPeriodLoading || isDashboardLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <div className="flex flex-col items-center gap-4">
          {/* O próprio anel gira (arco primário sobre a trilha cinza). Um ícone
              centralizado com translate + animate-spin sai do círculo: a
              animação sobrescreve o transform que o centralizava. */}
          <output
            aria-label="Carregando dashboard"
            className="block h-16 w-16 rounded-full border-4 border-muted border-t-primary animate-spin"
          />
          <div className="text-center">
            <p className="font-medium text-foreground">Carregando dashboard</p>
            <p className="text-sm text-muted-foreground">Preparando seus dados financeiros...</p>
          </div>
        </div>
      </div>
    );
  }

  const kpis = dashboardData?.kpis;

  // Process balance evolution data - backend returns period, planned, realized
  const balanceEvolutionData = dashboardData?.balance_evolution?.map((item) => ({
    date: item.period || 'N/A',
    balance: Number(item.realized) || 0,
    planned: Number(item.planned) || 0,
  })) || [];

  // Process future expenses progress - backend returns target, accumulated, progress_percentage
  const futureExpensesProgressData = dashboardData?.future_expenses_progress?.map((item) => ({
    name: item.name || 'N/A',
    target_amount: Number(item.target) || 0,
    current_value: Number(item.accumulated) || 0,
    progress: Number(item.progress_percentage) || 0,
    remaining: Math.max(0, (Number(item.target) || 0) - (Number(item.accumulated) || 0)),
    target_date: item.target_date,
    status: item.status,
  })) || [];

  return (
    <div className="min-h-screen bg-background">
      <div className="w-full px-4 sm:px-6 py-6 sm:py-8 space-y-6 sm:space-y-8">
        {/* Header */}
        <header className="flex flex-col">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Dashboard Financeiro
            </h1>
            <p className="text-muted-foreground mt-1">
              Visão geral do seu planejamento financeiro
            </p>
          </div>
          <div className="mt-8 flex items-center gap-3">
            {/* Navegação entre períodos - requer navegacao-de-periodo
                (sem a feature, o usuário vê apenas o período atual) */}
            <FeatureGuard feature="navegacao-de-periodo" fallback={null}>
              <PeriodNavigator
                currentPeriod={currentPeriod}
                periods={navigation?.periods || []}
                isLoading={isNavigating}
                onNavigate={navigatePeriod}
                variant="boxed"
              />
            </FeatureGuard>
          </div>
        </header>
        
        {/* Financial Summary Cards (KPIs) - requer kpis-financeiros */}
        <FeatureGuard
          feature="kpis-financeiros"
          fallback={
            <FeatureLockedCard
              title="KPIs Financeiros"
              description="Indicadores-chave: Saldo Atual, Receitas e Despesas Realizadas"
            />
          }
        >
          <FinancialSummaryCards periodId={currentPeriod?.id} />
        </FeatureGuard>

        {/* Secondary KPIs */}
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Realizado x Planejado KPI - requer planejado-x-realizado */}
          <FeatureGuard
            feature="planejado-x-realizado"
            fallback={
              <div className="bg-card rounded-xl border border-border/60 p-5 opacity-50">
                <div className="flex items-center gap-2 mb-3">
                  <PiggyBank className="h-5 w-5 text-muted-foreground" />
                  <h3 className="text-sm font-medium text-muted-foreground">Planejado x Realizado</h3>
                </div>
                <p className="text-2xl font-semibold text-muted-foreground">--</p>
              </div>
            }
          >
          {(() => {
            const realized = Number(kpis?.total_realized_expenses) || 0;
            const planned = Number(kpis?.total_planned_expenses) || 0;
            const isOverBudget = realized > planned;
            return (
              <div className={`relative bg-card rounded-xl border border-border/60 p-5 transition-all duration-200 hover:shadow-md hover:border-border group`}>
                <div className={`absolute top-0 left-4 right-4 h-0.5 rounded-full opacity-60 ${isOverBudget ? 'bg-rose-500' : 'bg-emerald-500'}`} />
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-3 flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium text-muted-foreground truncate">Despesas Realizado x Planejado</h3>
                    </div>
                    <div className={`text-2xl font-semibold tracking-tight ${isOverBudget ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                      R$ {realized.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      <span className="text-sm font-normal text-muted-foreground ml-1">
                        / R$ {planned.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    {isOverBudget && (
                      <p className="text-xs text-rose-600 dark:text-rose-400 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Você já gastou mais do que planejou. Cuidado!
                      </p>
                    )}
                    {!isOverBudget && (
                      <p className="text-xs text-muted-foreground">
                        {((realized / planned) * 100 || 0).toFixed(1)}% do orçamento utilizado
                      </p>
                    )}
                  </div>
                  <div className={`p-3 rounded-xl transition-transform duration-200 group-hover:scale-105 ${isOverBudget ? 'bg-rose-50 dark:bg-rose-950/30' : 'bg-emerald-50 dark:bg-emerald-950/30'}`}>
                    <PiggyBank className={`h-5 w-5 ${isOverBudget ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`} />
                  </div>
                </div>
              </div>
            );
          })()}
          </FeatureGuard>

          {/* Despesas Futuras Ativas - requer despesas-futuras */}
          <FeatureGuard 
            feature="despesas-futuras" 
            fallback={
              <div className="bg-card rounded-xl border border-border/60 p-5 opacity-50">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="h-5 w-5 text-muted-foreground" />
                  <h3 className="text-sm font-medium text-muted-foreground">Despesas Futuras</h3>
                </div>
                <p className="text-2xl font-semibold text-muted-foreground">--</p>
              </div>
            }
          >
            <KPICard
              title="Despesas Futuras Ativas"
              value={futureExpenses?.length || 0}
              format="number"
              icon={Target}
              subtitle={`${(Number(kpis?.future_expenses_progress) || 0).toFixed(1)}% progresso médio`}
              tooltip="Quantidade de metas de despesas futuras ativas"
            />
          </FeatureGuard>
          
          {/* Despesas Vencidas - requer despesas-mensais */}
          <FeatureGuard 
            feature="despesas-mensais" 
            fallback={
              <div className="bg-card rounded-xl border border-border/60 p-5 opacity-50">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-5 w-5 text-muted-foreground" />
                  <h3 className="text-sm font-medium text-muted-foreground">Despesas Vencidas</h3>
                </div>
                <p className="text-2xl font-semibold text-muted-foreground">--</p>
              </div>
            }
          >
            <KPICard
              title="Despesas Vencidas"
              value={overdueExpenses?.length || 0}
              format="number"
              icon={AlertTriangle}
              variant={overdueExpenses && overdueExpenses.length > 0 ? 'danger' : 'success'}
              tooltip="Despesas com data de vencimento ultrapassada"
            />
          </FeatureGuard>
          
          <KPICard
            title="Progresso do Período"
            value={Number(kpis?.progress_percentage) || 0}
            format="percentage"
            icon={Calendar}
            subtitle={`Dia ${(kpis?.days_elapsed || 0) + 1} de ${(kpis?.days_elapsed || 0) + (kpis?.days_remaining || 0)}`}
            tooltip="Quanto do período orçamentário já passou"
          />
        </section>

        {/* PRIMEIRA LINHA: Calendário Financeiro & Últimos Lançamentos */}
        <section className="grid gap-6 lg:grid-cols-2">
          {/* Calendário Financeiro - requer calendario-financeiro */}
          <FeatureGuard
            feature="calendario-financeiro"
            fallback={
              <FeatureLockedCard
                title="Calendário Financeiro"
                description="Visualização de 2 meses com destaque do período orçamentário"
              />
            }
          >
          <Card className="border-border/60">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-normal uppercase flex items-center gap-2 leading-[18px]">
                <Calendar className="h-5 w-5 text-primary" />
                CALENDÁRIO FINANCEIRO
              </CardTitle>
              <CardDescription>
                Visualização de 2 meses com destaque do período orçamentário
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DualCalendar
                currentPeriod={currentPeriod}
                selectedDate={selectedCalendarDate}
                onSelectDate={setSelectedCalendarDate}
                revenues={revenues?.map(r => ({
                  name: r.name,
                  expected_date: r.expected_date,
                  status: r.status || 'pending',
                  expected_amount: r.planned_amount,
                  realized_amount: r.realized_amount,
                })) || []}
                expenses={monthlyExpenses?.map(e => ({
                  name: e.name,
                  due_date: e.due_date,
                  status: e.status || 'pending',
                  expected_amount: e.planned_amount,
                  realized_amount: e.realized_amount,
                })) || []}
              />
            </CardContent>
          </Card>
          </FeatureGuard>

          {/* Recent Transactions - requer transacoes-recentes */}
          <FeatureGuard
            feature="transacoes-recentes"
            fallback={
              <FeatureLockedCard
                title="Transações Recentes"
                description="Lista das últimas transações realizadas"
              />
            }
          >
            <RecentTransactions limit={15} showSearch={true} periodId={currentPeriod?.id} />
          </FeatureGuard>
        </section>

        {/* SEGUNDA LINHA: Evolução do Saldo & Progresso das Despesas Futuras */}
        <section className="grid gap-6 lg:grid-cols-2">
          {/* Chart 3: Balance Evolution - Requer evolucao-do-saldo */}
          <FeatureGuard 
            feature="evolucao-do-saldo" 
            fallback={
              <Card className="border-border/60">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    Evolução do Saldo
                    <Lock className="h-3 w-3 text-amber-500" />
                  </CardTitle>
                  <CardDescription>Saldo diário ao longo do período orçamentário</CardDescription>
                </CardHeader>
                <CardContent>
                  <FeatureBlockedMessage featureName="Evolução do Saldo" />
                </CardContent>
              </Card>
            }
          >
            <Card className="border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">Evolução do Saldo</CardTitle>
                <CardDescription>Saldo diário ao longo do período orçamentário</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {balanceEvolutionData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={balanceEvolutionData}
                        margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                          axisLine={{ stroke: 'hsl(var(--border))' }}
                          tickLine={false}
                          interval="preserveStartEnd"
                          minTickGap={24}
                        />
                        <YAxis
                          tickFormatter={formatCurrency}
                          tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 12, paddingTop: 16 }} />
                        <Area
                          type="monotone"
                          dataKey="balance"
                          name="Saldo Realizado"
                          stroke={COLORS.primary}
                          strokeWidth={2}
                          fill="url(#balanceGradient)"
                        />
                        <Area
                          type="monotone"
                          dataKey="planned"
                          name="Saldo Planejado"
                          stroke={COLORS.muted}
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          fill="transparent"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                      <p className="text-sm">Sem dados disponíveis</p>
                      <p className="text-xs mt-1">Dados aparecerão após o início do período</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </FeatureGuard>

          {/* Chart 4: Future Expenses Progress - Requer progresso-de-metas */}
          <FeatureGuard 
            feature="progresso-de-metas" 
            fallback={
              <Card className="border-border/60">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    Progresso das Despesas Futuras
                    <Lock className="h-3 w-3 text-amber-500" />
                  </CardTitle>
                  <CardDescription>Acompanhamento das metas de despesas futuras</CardDescription>
                </CardHeader>
                <CardContent>
                  <FeatureBlockedMessage featureName="Progresso das Despesas Futuras" />
                </CardContent>
              </Card>
            }
          >
            <Card className="border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">Progresso das Despesas Futuras</CardTitle>
                <CardDescription>Acompanhamento das metas de despesas futuras</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] overflow-y-auto pr-2">
                  {futureExpensesProgressData.length > 0 ? (
                    <div className="space-y-4">
                      {futureExpensesProgressData.map((expense, index: number) => (
                        <div 
                          key={index}
                          className="p-4 rounded-lg border border-border/60 bg-muted/20 hover:bg-muted/40 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-primary/10">
                                <Target className="h-4 w-4 text-primary" />
                              </div>
                              <div>
                                <span className="font-medium text-foreground text-sm">{expense.name}</span>
                                <p className="text-xs text-muted-foreground">
                                  Objetivo: {formatCurrency(expense.target_amount)}
                                </p>
                              </div>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {formatBrasilia(expense.target_date, "dd MMM yyyy", 'N/A')}
                            </span>
                          </div>
                          <ProgressIndicator
                            value={expense.progress}
                            max={100}
                            showPercentage
                            size="sm"
                            colorScheme={esquemaDeProgresso(expense.progress)}
                          />
                          <div className="flex items-center justify-between mt-2 text-xs">
                            <span className="text-muted-foreground">
                              Falta: {formatCurrency(expense.remaining)}
                            </span>
                            <span
                              className={classeDeStatusDaMeta(expense)}
                            >
                              {rotuloDeStatusDaMeta(expense)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                      <Target className="h-12 w-12 text-muted-foreground/30 mb-3" />
                      <p className="text-sm font-medium">Nenhuma meta cadastrada</p>
                      <p className="text-xs mt-1">Crie metas para acompanhar seu progresso</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </FeatureGuard>
        </section>
      </div>
    </div>
  );
}

export default FinancialDashboard;
